import React, { useState, useEffect, useRef } from "react";
import "../housestyle.css";
import "./Rules.css";
import * as Icons from "../Icons/Icons";
import api from "../API/Api";

const MINECRAFT_RULES = [
  {
    category: "Gameplay",
    icon: "",
    rules: [
      {
        rule: "No cheating or hacking of any kind.",
        detail:
          "This includes X-ray, kill aura, fly hacks, speed hacks or any other client-side modifications that give an unfair advantage. Hacking results in a 3–5 week ban.",
      },
      {
        rule: "No X-ray texture packs or mods.",
        detail:
          "Using X-ray in any form resource pack or mod is a week ban on first offence.",
      },
      {
        rule: "No exploiting bugs or duplication glitches.",
        detail:
          "Report any exploits you find to staff immediately. Abusing them will result in a ban and item rollback.",
      },
      {
        rule: "No griefing other players' builds or bases.",
        detail:
          "Destroying, stealing from or sabotaging another player's property is not allowed outside of designated PvP zones.",
      },
    ],
  },
  {
    category: "In-game Chat",
    icon: "",
    rules: [
      {
        rule: "No spamming or excessive caps.",
        detail:
          "Repeating messages, flooding chat or using excessive capital letters will result in a mute.",
      },
      {
        rule: "No harassment or personal attacks.",
        detail:
          "Targeting players with insults, threats or hate speech is not tolerated under any circumstances.",
      },
      {
        rule: "No advertising other servers or websites.",
        detail:
          "Posting IP addresses, Discord invites or links to other servers results in an immediate permanent ban.",
      },
      {
        rule: "Keep chat in English.",
        detail:
          "Staff need to be able to moderate chat effectively. Other languages are allowed in private messages.",
      },
      {
        rule: "No sharing personal information.",
        detail:
          "Do not share your own or others' real-life personal information in public chat.",
      },
    ],
  },
  {
    category: "Punishment System (Every warning lasts 1 week)",
    icon: "",
    rules: [
      {
        rule: "1st offence Verbal warning.",
        detail:
          "A staff member will warn you in chat or via DM. The warning is logged.",
      },
      {
        rule: "2nd offence Second warning.",
        detail:
          "A second warning is issued.",
      },
      {
        rule: "3rd offence Temporary mute.",
        detail:
          "A temporary mute (1–24h).",
      },
      {
        rule: "4th offence Temporary ban.",
        detail:
          "A ban ranging from 1 day to 2 weeks depending on the rule broken.",
      },
      {
        rule: "Instant permanent ban offences.",
        detail:
          "X-ray, advertising other servers, sharing NSFW content or doxxing result in an immediate permanent ban. Hacking results in a 3–5 week ban.",
      },
    ],
  },
];

const DISCORD_RULES = [
  {
    category: "General Conduct",
    icon: "🛡️",
    rules: [
      {
        rule: "Be respectful to all members.",
        detail:
          "Treat everyone with basic respect. Harassment, discrimination or hate speech of any kind is not allowed.",
      },
      {
        rule: "No NSFW content anywhere.",
        detail:
          "This is a Minecraft community server. Explicit or NSFW content in any channel results in an immediate ban.",
      },
      {
        rule: "No doxxing or threatening other members.",
        detail:
          "Sharing personal information or making threats even as a joke will resolve into a timeout or a ban.",
      },
      {
        rule: "No impersonating staff or other members.",
        detail:
          "Using a similar name or avatar to mislead others is not allowed.",
      },
    ],
  },
  {
    category: "Channels",
    icon: "📋",
    rules: [
      {
        rule: "Keep discussions in the correct channel.",
        detail:
          "Use the appropriate channel for your topic. Off-topic messages will be deleted by staff.",
      },
      {
        rule: "No advertising in any channel, unless you partner with us.",
        detail:
          "Do not post links to other Discord servers, Minecraft servers or paid services without staff approval.",
      },
      {
        rule: "No spamming, emoji spam or wall-of-text posts.",
        detail:
          "Flooding any channel with repeated messages, excessive emojis or text will result in a timeout.",
      },
      {
        rule: "Use English in public channels.",
        detail:
          "Staff need to be able to moderate public channels. Other languages are fine in DMs.",
      },
    ],
  },
  {
    category: "Punishment System (Every warning lasts 1 week)",
    icon: "⚖️",
    rules: [
      {
        rule: "1st offence Warning from staff.",
        detail:
          "A staff member will issue a formal warning via DM or in the channel. It is logged.",
      },
      {
        rule: "2nd offence Temporary mute (1–24h).",
        detail:
          "You will be muted across all channels for a period decided by staff.",
      },
      {
        rule: "3rd offence Temporary mute (1–7 days).",
        detail:
          "You will be muted across all channels for a period decided by staff.",
      },
      {
        rule: "4th offence Temporary ban (1-7 days).",
        detail:
          "Continued rule breaking results in a temporary Discord ban. Appeal via a support ticket if you believe it was unjust.",
      },
      {
        rule: "Instant permanent ban offences.",
        detail:
          "NSFW content, doxxing, server advertising or threats result in an immediate permanent ban with no warnings.",
      },
    ],
  },
];

const video = 'https://pub-332780ae20b540038a9ea772f1bf747c.r2.dev/background_vids/vid_1.mp4';

function RuleRow({ num, rule, detail }) {
  return (
    <div className="rule-row">
      <div className="rule-row-main">
        <span className="rule-num">{num}</span>
        <span className="rule-text">{rule}</span>
      </div>
      <div className="rule-detail">{detail}</div>
    </div>
  );
}

function RuleSection({ section, startNum }) {
  return (
    <div className="rule-section">
      <div className="rule-section-header">
        <span className="rule-section-icon">{section.icon}</span>
        <span className="rule-section-title">{section.category}</span>
      </div>
      <div className="rule-list card-nohover">
        {section.rules.map((r, i) => (
          <RuleRow key={i} num={startNum + i} rule={r.rule} detail={r.detail} />
        ))}
      </div>
    </div>
  );
}

const EGG_WEBHOOK = process.env.REACT_APP_WEBHOOK_EASTER_EGG;
const EGG_LS_KEY = 'smc_egg_claimed';

function EasterEggModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [discordTag, setDiscordTag] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(() => !!localStorage.getItem(EGG_LS_KEY));
  const inputRef = useRef(null);

  useEffect(() => {
    api("geteggstatus").then((res) => {
      if (res?.data) setStatus(res.data);
    });
    if (!localStorage.getItem(EGG_LS_KEY)) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, []);

  const slotsLeft = status ? status.max - status.claimed : null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!discordTag.trim() || sending) return;
    setSending(true);
    setError("");

    const res = await api("claimegg", { discord_tag: discordTag.trim() });
    if (!res?.isSuccess) {
      setError(res?.message || "Something went wrong, try again.");
      setSending(false);
      return;
    }

    if (res.data) setStatus(res.data);

    const slot = res.data?.claimed ?? "?";
    const max = res.data?.max ?? 5;
    const remaining = max - slot;
    await fetch(EGG_WEBHOOK, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        content: [
          "**Secret Discovered!**",
          "",
          "Someone found the hidden secret on our website!",
          "",
          `🥇 **@${discordTag.trim()}** found the secret!`,
          "🎁 **Double Chance** in the next giveaway",
          "",
          "Congrats! 🎉",
          remaining > 0
            ? `Stay close, there ${remaining === 1 ? "is" : "are"} still **${remaining}** spot${remaining === 1 ? "" : "s"} left`
            : "All spots have been claimed! 🔒",
          `-# Slot ${slot}/${max} - simplemcnetwork.net`,
        ].join("\n"),
      }),
    }).catch(() => { });

    localStorage.setItem(EGG_LS_KEY, '1');
    setSending(false);
    setDone(true);
  }

  return (
    <div className="egg-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="egg-modal">
        <button className="egg-close" onClick={onClose}>✕</button>

        <div className="egg-eyebrow">Secret found</div>
        <div className="egg-title">You found a secret!</div>

        {done ? (
          <div className="egg-success-block">
            <div className="egg-success-icon">✓</div>
            <div className="egg-success-label">Claimed!</div>
            <p className="egg-success-desc">
              Check the Discord server! your name will be announced and you'll have <strong>double chance</strong> in the next giveaway.
            </p>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <p className="egg-desc">
              Submit your Discord tag to receive <strong>double chance</strong> in the next giveaway.{" "}
              {slotsLeft !== null && (
                <span className="egg-slots">
                  {slotsLeft} of {status.max} slots left.
                </span>
              )}
            </p>

            {status?.full ? (
              <div className="apply-error">All slots have been claimed. Better luck next time!</div>
            ) : (
              <form className="egg-form" onSubmit={handleSubmit}>
                {error && <div className="apply-error">{error}</div>}
                <div className="apply-field">
                  <div className="apply-field-label">Discord Tag <span className="apply-req"> </span></div>
                  <input
                    ref={inputRef}
                    className="apply-input"
                    type="text"
                    placeholder="e.g. username or username#1234"
                    value={discordTag}
                    onChange={(e) => { setDiscordTag(e.target.value); setError(""); }}
                    maxLength={100}
                    disabled={sending}
                  />
                </div>
                <div className="apply-submit-row">
                  <button
                    type="submit"
                    className={`btn btn-primary ${(sending || !discordTag.trim()) ? "apply-btn-disabled" : ""}`}
                    disabled={sending || !discordTag.trim()}
                  >
                    {sending ? "Claiming…" : "Claim reward"}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Rules() {
  const [tab, setTab] = useState("minecraft");
  const [eggOpen, setEggOpen] = useState(false);
  const alreadyClaimed = !!localStorage.getItem(EGG_LS_KEY);
  const rules = tab === "minecraft" ? MINECRAFT_RULES : DISCORD_RULES;

  let counter = 1;

  return (
    <>
      {eggOpen && <EasterEggModal onClose={() => setEggOpen(false)} />}
      <div className="page-hero">
        <video className="page-hero-video" src={video} autoPlay loop muted playsInline />
        <div className="page-hero-overlay" />
        <div className="page-hero-content container">
          <div className="page-hero-eyebrow">Server</div>
          <div className="rules-title-row rules-title-hero">
            <h1 className={tab === "minecraft" ? "active" : ""} onClick={() => setTab("minecraft")}>
              Minecraft<em>Rules</em>
            </h1>
            <h1 className={tab === "discord" ? "active" : ""} onClick={() => setTab("discord")}>
              Discord<em>Rules</em>
            </h1>
          </div>
          <p className="page-hero-sub">Please follow our rules to keep the server friendly and fun for everyone!</p>
        </div>
      </div>

      <div className="container section">
        <div className="rules-tab-bar">
          <button
            className={`btn ${tab === "minecraft" ? "btn-teal" : "btn-secondary"}`}
            onClick={() => setTab("minecraft")}
          >
            <img
              src={Icons.shield}
              alt="Minecraft Icon"
              width={"24px"}
            />
            Minecraft
          </button>
          <button
            className={`btn ${tab === "discord" ? "btn-teal" : "btn-secondary"} rules-discord-btn`}
            onClick={() => setTab("discord")}
          >
            <img src={Icons.discord_white} alt="Discord Icon" width={"24px"} />
            Discord
          </button>
        </div>

        <div className="rules-grid">
          {rules.map((section) => {
            const start = counter;
            counter += section.rules.length;
            return (
              <RuleSection
                key={section.category}
                section={section}
                startNum={start}
              />
            );
          })}
        </div>

        {tab === "minecraft" && (
          <div className="bug-report-banner card-raised fade-up">
            <div className="bug-report-left">
              <div className="bug-report-icon">🐛</div>
              <div>
                <div className="bug-report-title">Found a bug?</div>
                <p className="bug-report-desc">
                  If you discover a <strong>game-breaking bug</strong> or
                  exploit, report it to staff immediately via a Discord ticket —
                  do not abuse it. Players who report a significant bug will be{" "}
                  <span className={`egg-trigger${alreadyClaimed ? " egg-trigger-claimed" : ""}`} onClick={() => setEggOpen(true)}>rewarded</span> with the exclusive{" "}
                  <span className="bug-tag">BUG</span> in-game tag as a thank
                  you.
                </p>
                <div className="bug-report-steps">
                  <div className="bug-step">
                    <span className="bug-step-num">1</span>Open a ticket in
                    Discord
                  </div>
                  <div className="bug-step">
                    <span className="bug-step-num">2</span>Describe the bug
                    clearly
                  </div>
                  <div className="bug-step">
                    <span className="bug-step-num">3</span>Do not share
                    it publicly
                  </div>
                  <div className="bug-step">
                    <span className="bug-step-num">4</span>Receive your{" "}
                    <span className="bug-tag">BUG</span> tag in-game
                  </div>
                </div>
              </div>
            </div>
            <button
              className="btn btn-primary bug-report-btn"
              onClick={() =>
                window.open("https://discord.com/invite/simplemc", "_blank")
              }
            >
              Open a Ticket
            </button>
          </div>
        )}
      </div>
    </>
  );
}
